import { useState, useEffect } from 'react';
import {
    Upload, FileText, Mic, HelpCircle, Plus, Trash2,
    ChevronDown, ChevronUp, Check, Loader2, BookOpen
} from 'lucide-react';
import { ope } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import ConfirmModal from '../ConfirmModal';

// Secciones extraídas para simplificar el God Object
import OpeAdminPdfUploader from './admin-sections/OpeAdminPdfUploader';
import OpeAdminSummaryGenerator from './admin-sections/OpeAdminSummaryGenerator';
import OpeAdminAudioController from './admin-sections/OpeAdminAudioController';
import OpeAdminQuestionManager from './admin-sections/OpeAdminQuestionManager';
import OpeAdminRetoConfig from './admin-sections/OpeAdminRetoConfig';
import OpeAdminRetoStats from './admin-sections/OpeAdminRetoStats';
import { BarChart3 } from 'lucide-react';

const OpeAdminTab = () => {
    const [temas, setTemas] = useState([]);
    const [selectedTema, setSelectedTema] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedSection, setExpandedSection] = useState(null); // 'pdf' | 'resumen' | 'audio' | 'preguntas'
    const { success, error } = useToast();

    // Estado nuevo tema
    const [showNewTema, setShowNewTema] = useState(false);
    const [newTema, setNewTema] = useState({ titulo: '', descripcion: '' });

    // Estado modales
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        temaId: null,
        temaTitulo: ''
    });

    useEffect(() => {
        loadTemas();
    }, []);

    const loadTemas = async () => {
        try {
            setLoading(true);
            const data = await ope.adminGetTemas();
            setTemas(data);
            setSelectedTema(prev => {
                if (!prev) return prev;
                const updated = data.find(t => t.id === prev.id);
                return updated ? { ...prev, ...updated } : prev;
            });
        } catch {
            error('Error al cargar temas');
        } finally {
            setLoading(false);
        }
    };

    const selectTema = (tema) => {
        setSelectedTema(tema);
        setExpandedSection(null);
    };

    // ─── CRUD Temas ──────────────────────────────────
    const handleCreateTema = async () => {
        if (!newTema.titulo.trim()) return;
        try {
            await ope.adminCreateTema(newTema);
            success('Tema creado correctamente');
            setNewTema({ titulo: '', descripcion: '' });
            setShowNewTema(false);
            loadTemas();
        } catch {
            error('Error al crear tema');
        }
    };

    const confirmDeleteTema = async () => {
        const { temaId } = confirmModal;
        if (!temaId) return;
        try {
            await ope.adminDeleteTema(temaId);
            success('Tema eliminado');
            if (selectedTema?.id === temaId) setSelectedTema(null);
            loadTemas();
            closeConfirmModal();
        } catch {
            error('Error al eliminar tema');
        }
    };

    const openDeleteTemaConfirm = (tema) => {
        setConfirmModal({
            isOpen: true,
            temaId: tema.id,
            temaTitulo: tema.titulo
        });
    };

    const closeConfirmModal = () => {
        setConfirmModal({
            isOpen: false,
            temaId: null,
            temaTitulo: ''
        });
    };

    const toggleSection = (section) => {
        if (expandedSection === section) {
            setExpandedSection(null);
        } else {
            setExpandedSection(section);
        }
    };

    // ─── Pipeline Status Badge ───────────────────────
    const PipelineBadge = ({ tema }) => {
        const steps = [
            { key: 'pdf', label: '📄', done: !!tema.pdf_filename },
            { key: 'texto', label: '📝', done: tema.texto_chars > 0 },
            { key: 'resumen', label: '📋', done: !!tema.tiene_resumen },
            { key: 'audio', label: '🔊', done: tema.num_audios > 0 },
            { key: 'preguntas', label: '❓', done: tema.num_publicadas > 0 },
        ];
        return (
            <div className="flex gap-1">
                {steps.map((step) => (
                    <span key={step.key} className={`text-sm ${step.done ? 'opacity-100' : 'opacity-30'}`}
                        title={step.done ? `${step.key} ✓` : `${step.key} pendiente`}>
                        {step.label}
                    </span>
                ))}
            </div>
        );
    };

    // ─── Section Header ───────────────────────────────
    const SectionHeader = ({ icon: Icon, title, section, disabled, badge }) => (
        <button
            onClick={() => !disabled && toggleSection(section)}
            disabled={disabled}
            className={`w-full flex items-center justify-between p-4 rounded-xl transition-all
                ${expandedSection === section ? 'bg-indigo-600/20 border-indigo-500/50' : 'bg-[var(--glass-bg)] hover:bg-[var(--glass-border)] border-[var(--glass-border)]'}
                ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                border`}
        >
            <div className="flex items-center gap-3">
                <Icon className="w-5 h-5 text-indigo-500" />
                <span className="font-medium text-[var(--text-primary)]">{title}</span>
                {badge && <span className="text-xs px-2 py-0.5 bg-indigo-600/30 rounded-full text-indigo-300">{badge}</span>}
            </div>
            {expandedSection === section ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* ── Configuración Reto Diario ──────── */}
            <div className="space-y-3">
                <SectionHeader
                    icon={BookOpen}
                    title="Configuración Reto Diario"
                    section="reto_config"
                />
                {expandedSection === 'reto_config' && (
                    <OpeAdminRetoConfig />
                )}
            </div>

            {/* ── Estadísticas Reto Diario ──────── */}
            <div className="space-y-3">
                <SectionHeader
                    icon={BarChart3}
                    title="Estadísticas Reto Diario"
                    section="reto_stats"
                />
                {expandedSection === 'reto_stats' && (
                    <OpeAdminRetoStats />
                )}
            </div>

            {/* ── Lista de Temas ─────────────────── */}
            <div className="space-y-3">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-[var(--text-primary)]">Temas del Temario</h3>
                    <button
                        onClick={() => setShowNewTema(!showNewTema)}
                        className="flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm transition-colors"
                    >
                        <Plus className="w-4 h-4" /> Nuevo Tema
                    </button>
                </div>

                {/* Formulario nuevo tema */}
                {showNewTema && (
                    <div className="p-4 bg-[var(--glass-bg)] rounded-xl border border-[var(--glass-border)] space-y-3">
                        <input
                            type="text"
                            placeholder="Título del tema"
                            value={newTema.titulo}
                            onChange={e => setNewTema(prev => ({ ...prev, titulo: e.target.value }))}
                            className="w-full px-3 py-2 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-indigo-500"
                        />
                        <input
                            type="text"
                            placeholder="Descripción (opcional)"
                            value={newTema.descripcion}
                            onChange={e => setNewTema(prev => ({ ...prev, descripcion: e.target.value }))}
                            className="w-full px-3 py-2 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-indigo-500"
                        />
                        <div className="flex gap-2">
                            <button onClick={handleCreateTema} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm flex items-center gap-2">
                                <Check className="w-4 h-4" /> Crear
                            </button>
                            <button onClick={() => setShowNewTema(false)} className="px-4 py-2 bg-[var(--glass-border)] hover:bg-gray-500 text-[var(--text-primary)] rounded-lg text-sm">
                                Cancelar
                            </button>
                        </div>
                    </div>
                )}

                {/* Tabla de temas */}
                <div className="space-y-2">
                    {temas.map(tema => (
                        <div
                            key={tema.id}
                            onClick={() => selectTema(tema)}
                            className={`p-4 rounded-xl border cursor-pointer transition-all
                                ${selectedTema?.id === tema.id
                                    ? 'bg-indigo-600/10 border-indigo-500/50 ring-1 ring-indigo-500/30'
                                    : 'bg-[var(--glass-bg)] border-[var(--glass-border)] hover:bg-[var(--glass-border)]'}`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3">
                                        <span className="text-[var(--text-primary)] font-medium">{tema.titulo}</span>
                                        <PipelineBadge tema={tema} />
                                    </div>
                                    <div className="flex gap-4 mt-1 text-xs text-gray-500">
                                        {tema.pdf_filename && <span>📄 PDF subido</span>}
                                        {tema.texto_chars > 0 && <span>📝 {tema.texto_chars.toLocaleString()} chars</span>}
                                        {tema.num_preguntas > 0 && (
                                            <span>❓ {tema.num_publicadas}/{tema.num_preguntas} publicadas</span>
                                        )}
                                        {tema.num_audios > 0 && <span>🔊 Audio listo</span>}
                                    </div>
                                </div>
                                <button
                                    onClick={(e) => { e.stopPropagation(); openDeleteTemaConfirm(tema); }}
                                    className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                                    title="Eliminar tema"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── Pipeline del Tema Seleccionado ─── */}
            {selectedTema && (
                <div className="space-y-3 border-t border-[var(--glass-border)] pt-6">
                    <h3 className="text-lg font-semibold text-[var(--text-primary)] flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-indigo-500" />
                        Pipeline: {selectedTema.titulo}
                    </h3>

                    {/* ── 1. Subir PDF ─────────────────── */}
                    <SectionHeader
                        icon={Upload}
                        title="1. Subir PDF"
                        section="pdf"
                        badge={selectedTema.pdf_filename ? '✓ Subido' : null}
                    />
                    {expandedSection === 'pdf' && (
                        <OpeAdminPdfUploader
                            temaId={selectedTema.id}
                            pdfFilename={selectedTema.pdf_filename}
                            textoChars={selectedTema.texto_chars}
                            onUploadSuccess={loadTemas}
                        />
                    )}

                    {/* ── 2. Generar Resumen ───────────── */}
                    <SectionHeader
                        icon={FileText}
                        title="2. Generar Resumen / Guión"
                        section="resumen"
                        disabled={!selectedTema.texto_chars}
                        badge={selectedTema.tiene_resumen ? '✓ Generado' : null}
                    />
                    {expandedSection === 'resumen' && (
                        <OpeAdminSummaryGenerator
                            temaId={selectedTema.id}
                            tieneResumen={selectedTema.tiene_resumen}
                            onSummaryGenerated={loadTemas}
                        />
                    )}

                    {/* ── 3. Generar Audio ─────────────── */}
                    <SectionHeader
                        icon={Mic}
                        title="3. Generar Audio (edge-tts)"
                        section="audio"
                        disabled={!selectedTema.tiene_resumen}
                        badge={selectedTema.num_audios > 0 ? '✓ Generado' : null}
                    />
                    {expandedSection === 'audio' && (
                        <OpeAdminAudioController
                            temaId={selectedTema.id}
                            numAudios={selectedTema.num_audios}
                            onAudioGenerated={loadTemas}
                        />
                    )}

                    {/* ── 4. Generar Preguntas ─────────── */}
                    <SectionHeader
                        icon={HelpCircle}
                        title="4. Generar Preguntas"
                        section="preguntas"
                        disabled={!selectedTema.texto_chars}
                        badge={selectedTema.num_preguntas > 0 ? `${selectedTema.num_publicadas}/${selectedTema.num_preguntas}` : null}
                    />
                    {expandedSection === 'preguntas' && (
                        <OpeAdminQuestionManager
                            temaId={selectedTema.id}
                            textoChars={selectedTema.texto_chars}
                            onQuestionsUpdated={loadTemas}
                        />
                    )}
                </div>
            )}

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={closeConfirmModal}
                onConfirm={confirmDeleteTema}
                title="Eliminar tema"
                message={`¿Eliminar el tema "${confirmModal.temaTitulo}" y todo su contenido asociado (audios y preguntas)?`}
                confirmText="Eliminar"
                cancelText="Cancelar"
                variant="danger"
            />
        </div>
    );
};

export default OpeAdminTab;
